<template>
    <div class="px-4 sm:px-6 lg:px-8 py-8">
        <div class="sm:flex sm:items-center sm:justify-between">
            <div class="sm:flex-auto">
                <h1 class="text-2xl font-semibold text-gray-900">Obrat z objednávok</h1>
                <p class="mt-2 text-sm text-gray-700">Prehľad obratu z uhradených objednávok</p>
            </div>
        </div>

        <!-- Date Filter -->
        <div class="mt-6 bg-white shadow rounded-lg p-6">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                    <label for="start-date" class="block text-sm font-medium text-gray-700">Od dátumu</label>
                    <input type="date" id="start-date" v-model="filters.start_date"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div>
                    <label for="end-date" class="block text-sm font-medium text-gray-700">Do dátumu</label>
                    <input type="date" id="end-date" v-model="filters.end_date"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                <div class="flex flex-col justify-end gap-1">
                    <p v-if="filters.start_date !== filters.end_date && (!filters.start_date || !filters.end_date)"
                        class="text-xs text-amber-600">Pre filtrovanie zadajte oba dátumy</p>
                    <div class="flex gap-2">
                        <button @click="applyFilters" type="button"
                            :disabled="!bothDatesSet && (!!filters.start_date || !!filters.end_date)"
                            :class="[
                                'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
                                (!bothDatesSet && (!!filters.start_date || !!filters.end_date))
                                    ? 'bg-indigo-300 cursor-not-allowed'
                                    : 'bg-indigo-600 hover:bg-indigo-700'
                            ]">
                            Filtrovať
                        </button>
                        <button @click="clearFilters" type="button"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Resetovať
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="mt-8 flex justify-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>

        <div v-else>
            <!-- Summary Cards -->
            <div class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2" :class="isFiltered ? 'lg:grid-cols-2' : 'lg:grid-cols-4'">
                <!-- Total profit -->
                <div class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">
                                        {{ isFiltered ? 'Obrat za vybrané obdobie' : 'Celkový obrat' }}
                                    </dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ formatEur(data.total_profit) }}</dd>
                                    <dd v-if="isFiltered" class="text-xs text-gray-500 mt-1">
                                        {{ formatDate(appliedDates.start) }} – {{ formatDate(appliedDates.end) }}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Total count -->
                <div class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">
                                        {{ isFiltered ? 'Počet objednávok za obdobie' : 'Počet uhradených objednávok' }}
                                    </dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ data.total_count }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Current month (only when no date filter) -->
                <div v-if="!isFiltered" class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M8 7V3m8 4V3m-9 8h10m-9 4h6M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">Obrat tento mesiac</dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ formatEur(data.current_month_profit) }}</dd>
                                    <dd class="text-xs text-gray-500 mt-1">{{ getCurrentMonthName() }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Previous month (only when no date filter) -->
                <div v-if="!isFiltered" class="bg-white overflow-hidden shadow rounded-lg">
                    <div class="p-5">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-5 w-0 flex-1">
                                <dl>
                                    <dt class="text-sm font-medium text-gray-500 truncate">Obrat minulý mesiac</dt>
                                    <dd class="text-3xl font-semibold text-gray-900">{{ formatEur(data.prev_month_profit) }}</dd>
                                    <dd class="text-xs text-gray-500 mt-1">{{ getPreviousMonthName() }}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Paid Orders Table -->
        <div class="mt-8">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Uhradené objednávky</h2>
            <OrderTable
                :isPaidOnly="true"
                :filterStartDate="appliedDates.start"
                :filterEndDate="appliedDates.end"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import OrderDataService from '@/services/OrderDataService'
import OrderTable from '@/components/OrderTable.vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

interface ProfitData {
    total_profit: number
    total_count: number
    current_month_profit: number
    prev_month_profit: number
}

export default defineComponent({
    name: 'OrderProfitView',
    components: { OrderTable },
    data() {
        return {
            loading: true,
            filters: {
                start_date: '',
                end_date: '',
            },
            appliedDates: {
                start: '',
                end: '',
            },
            data: {
                total_profit: 0,
                total_count: 0,
                current_month_profit: 0,
                prev_month_profit: 0,
            } as ProfitData,
        }
    },
    computed: {
        bothDatesSet(): boolean {
            return !!(this.filters.start_date && this.filters.end_date)
        },
        isFiltered(): boolean {
            return !!(this.appliedDates.start && this.appliedDates.end)
        },
    },
    mounted() {
        this.loadProfit()
    },
    methods: {
        async loadProfit() {
            this.loading = true
            try {
                const filters = this.bothDatesSet ? this.filters : undefined
                const response = await OrderDataService.getPaidOrdersProfit(filters)
                if (response.data.success) {
                    this.data = response.data.data
                }
            } catch (error) {
                console.error('Error loading profit data:', error)
                toast.error('Chyba pri načítavaní údajov o obrate')
            } finally {
                this.loading = false
            }
        },
        applyFilters() {
            if (!this.bothDatesSet) return
            this.appliedDates.start = this.filters.start_date
            this.appliedDates.end = this.filters.end_date
            this.loadProfit()
        },
        clearFilters() {
            this.filters.start_date = ''
            this.filters.end_date = ''
            this.appliedDates.start = ''
            this.appliedDates.end = ''
            this.loadProfit()
        },
        formatEur(value: number | undefined): string {
            if (value === undefined || value === null) return '0,00 €'
            return Number(value).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
        },
        formatDate(dateStr: string): string {
            if (!dateStr) return ''
            const [y, m, d] = dateStr.split('-')
            return `${d}.${m}.${y}`
        },
        getCurrentMonthName(): string {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            return months[new Date().getMonth()]
        },
        getPreviousMonthName(): string {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            const prev = new Date().getMonth() - 1
            return months[prev < 0 ? 11 : prev]
        },
    },
})
</script>
